import { HStack } from "@chakra-ui/react";
import { UserApi } from "@radianse/services";
import mergeWith from "lodash.mergewith";
import React, { useContext } from "react";
import Button from "../components/Button";
import ErrorMsg from "../components/Error";
import Loader from "../components/Loader";
import { useTheme } from "../context/ThemeContext";

const appPermissionId = "05a2a731-8de4-4913-9b5b-1a51e7054459";

export const UserApiClient = new UserApi(
  "1.0",
  `${window["_env"].API_URL}/app/users`
);

export const AuthContext = React.createContext(undefined);

function uniquePerm(objValue, srcValue) {
  return Array.from(new Set(srcValue.concat(objValue))).filter(Boolean);
}

function getAllowedRoutes(routes, routePermissions) {
  if (!routes) return null;

  return routes.filter(({ id, permissions, path, nav }) => {
    if (id === "*") return true;
    if (!routePermissions || !path || !nav) return false;

    const appPermission =
      routePermissions[appPermissionId] &&
      routePermissions[appPermissionId].some(
        (p) => permissions && permissions.includes(p)
      );

    // check application for required access permissions
    const requiredPermissions =
      routePermissions[id] &&
      routePermissions?.[id].some(
        (p) => permissions && permissions.includes(p)
      );
    return appPermission || requiredPermissions;
  });
}

export function AuthProvider({ children, privateRouteConfig }) {
  const [, switchTheme] = useTheme();
  const [authState, setAuth] = React.useState({
    status: "pending",
    error: null,
    user: null,
  });
  const onRetry = () => {
    window.location.reload();
  };
  const onLogout = () => {
    window.location.href = window.location.origin + "/account/logout";
  };
  const userPermissions = (user) => {
    return mergeWith(
      user?.tenantPermissionsPerApp,
      user?.groupPermissionsPerApp,
      user?.customerPermissionsPerApp,
      uniquePerm
    );
  };

  const userRoutes = (permissions) => {
    return getAllowedRoutes(privateRouteConfig, permissions);
  };

  React.useEffect(() => {
    UserApiClient.userMeGet().then(
      (res) => {
        // TODO: Theme selection should be defined in the api
        switchTheme(
          res.data.data.customerName.replace(/\s/g, "").toLowerCase()
        );
        const permissions = userPermissions(res.data.data);
        const routes = userRoutes(permissions);
        if (!Object.keys(permissions).length) {
          setAuth({
            status: "error",
            error: {
              message:
                "Failed to sync user permissions. Please try again, or contact Radianse Support at 603-994-2222.",
            },
            user: null,
          });
        } else {
          setAuth({
            status: "success",
            error: null,
            user: {
              ...res.data.data,
              routes,
              permissions,
            },
          });
        }
      },
      (error) => {
        setAuth({ status: "error", error, user: null });
      }
    );
  }, []);
  return (
    <AuthContext.Provider value={authState}>
      {authState.status === "pending" ? (
        <Loader text={"Logging in..."} />
      ) : authState.status === "error" ? (
        <ErrorMsg
          title={"Authentication failed"}
          message={authState.error.message}
        >
          <HStack spacing={10}>
            <Button onClick={onRetry} variant={"primary"} size="sm">
              Retry
            </Button>
            <Button onClick={onLogout} variant={"secondary"} size="sm">
              Logout
            </Button>
          </HStack>
        </ErrorMsg>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuthState() {
  const authState = React.useContext(AuthContext);

  const isPending = authState.status === "pending";
  const isError = authState.status === "error";
  const isSuccess = authState.status === "success";
  const isAuthenticated = authState.user && isSuccess;
  return {
    ...authState,
    isPending,
    isError,
    isSuccess,
    isAuthenticated,
  };
}

export function useUser() {
  const { user } = React.useContext(AuthContext);
  return user;
}

export function useCustomer() {
  const {
    user: { customerId, customerName },
  } = React.useContext(AuthContext);
  return { customerId, customerName };
}

export function useTenant() {
  const {
    user: { tenantId, tenantName },
  } = React.useContext(AuthContext);
  return { tenantId, tenantName };
}

export function useGroup() {
  const {
    user: { groupId, groupName },
  } = React.useContext(AuthContext);
  return { groupId, groupName };
}

export function useUserRoutes() {
  const {
    user: { routes },
  } = React.useContext(AuthContext);
  return routes;
}

export function useRoles(scope) {
  const { user } = React.useContext(AuthContext);
  switch (scope) {
    case "Tenant":
      return new Set([
        ...(user?.tenantRoles || []),
        ...(user?.groupRoles || []),
        ...(user?.customerRoles || []),
      ]);
    case "Group":
      return new Set([
        ...(user?.customerRoles || []),
        ...(user?.groupRoles || []),
      ]);
    case "Customer":
      return new Set([...(user?.customerRoles || [])]);
    default:
      return new Set([
        ...(user?.tenantRoles || []),
        ...(user?.groupRoles || []),
        ...(user?.customerRoles || []),
      ]);
  }
}
export function usePermissions(appId) {
  const {
    user: { permissions },
  } = React.useContext(AuthContext);
  if (appId) return permissions[appId];
  return permissions;
}

/**
 * Returns a boolean based on the users permissions
 * @param {string|object} permissionNames string or array of strings representing the desired permission
 * @returns {boolean}
 */
export function useHasPermissions(permissionNames) {
  const {
    user: { permissions },
  } = useContext(AuthContext);

  if (!permissions) {
    return false;
  }

  const flatPermissions = Object.keys(permissions).flatMap(
    (app) => permissions[app]
  );

  if (typeof permissionNames === "string") {
    return flatPermissions.includes?.(permissionNames);
  } else if (Array.isArray(permissionNames)) {
    return flatPermissions.some((permissionName) =>
      Boolean(permissionNames.includes?.(permissionName))
    );
  } else {
    return false;
  }
}

export function useHasPermissionsGroup(permissionGroup) {
  const {
    user: { permissions },
  } = useContext(AuthContext);

  if (!permissions || typeof permissionGroup !== "object") {
    return false;
  }

  const flatPermissions = Object.keys(permissions).flatMap(
    (app) => permissions[app]
  );

  const allowedPermissions = {};

  Object.keys(permissionGroup).forEach((propertyName) => {
    allowedPermissions[propertyName] = flatPermissions.includes?.(
      permissionGroup[propertyName]
    );
  });

  return allowedPermissions;
}
